import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
    Box,
    Container,
    Card,
    CardContent,
    Tabs,
    Tab,
    Typography,
    MenuItem,
    FormControl,
    Select,
    InputLabel,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import Speedometer from "react-d3-speedometer";
import Grade from "./grade.svg";
import Name from "./name.svg";
import Section from "./section.svg";
import Username from "./username.svg";
import Navbar from "../../AdminHeader/AdminHeader";
import SideBar from "../../AdminSidebar/AdminSidebar";
import { API_LoginURL } from "../../../../../config";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
    },
    scales: {
        y: {
            beginAtZero: true,
            max: 100,
        },
    },
};

const yearOptions = [2024, 2025, 2026];

function Dashboard() {
    const location = useLocation();
    const student = location.state?.student;

    const stat = [
        { title: "Name", val: student?.firstName || "N/A", icon: Name },
        { title: "Username", val: student?.userName || "N/A", icon: Username },
        { title: "Grade", val: student?.className || "N/A", icon: Grade },
        { title: "School", val: student?.schoolName || "N/A", icon: Section },
    ];

    const [selectedTab, setSelectedTab] = useState(0); // Default to Final Exam
    const [selectedYear, setSelectedYear] = useState(2025);
    const [assessmentData, setAssessmentData] = useState(null);
    const [userId, setUserId] = useState(null);
    const [error, setError] = useState("");

    const tabKeys = ["initial", "detailed", "halfYearly", "final"];
    const selectedKey = tabKeys[selectedTab] || "initial";

    const headers = {
        headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("token"),
        },
    };

    const transformAPIData = (apiData) => {
        const defaultSubjects = [
            "Science",
            "Technology",
            "Engineering",
            "Mathematics",
            "AI",
        ];

        const buildBarData = (records) => {
            const dataMap = {};
            defaultSubjects.forEach((subject) => {
                dataMap[subject] = 0;
            });

            records.forEach(({ Subject, MarksObtained }) => {
                dataMap[Subject] = MarksObtained;
            });

            const values = defaultSubjects.map((subj) => dataMap[subj]);
            const total = values.reduce((sum, val) => sum + val, 0);
            const count = values.filter((val) => val > 0).length;
            const average = count ? Math.round(total / count) : 0;

            return {
                data: {
                    labels: defaultSubjects,
                    datasets: [
                        {
                            label: "Score",
                            data: values,
                            backgroundColor: [
                                "#FF8C00",
                                "#FF6347",
                                "#3CB371",
                                "#9370DB",
                                "#FFA07A",
                            ],
                        },
                    ],
                },
                average,
            };
        };

        return {
            initial: {
                title: "Initial Assessment",
                ...buildBarData(apiData.Initial || []),
            },
            detailed: {
                title: "Detailed Assessment",
                ...buildBarData(apiData.Combined || []),
            },
            halfYearly: {
                title: "Half Yearly Exam",
                ...buildBarData(apiData["Half Year"] || []),
            },
            final: {
                title: "Final Exam",
                ...buildBarData(apiData.Final || []),
            },
        };
    };

    const fetchUserId = async () => {
        try {
            const res = await axios.get(
                `${API_LoginURL}User/UserList`,
                headers
            );
            const users = res.data || [];
            const matchedUser = users.find(
                (u) => u.userName === student?.userName
            );
            if (matchedUser) {
                setUserId(matchedUser.userId);
                setError("");
            } else {
                setError("User not found in user list.");
                setUserId(null);
            }
        } catch (err) {
            console.error("Error fetching user list:", err);
            setError("Failed to fetch user list.");
        }
    };

    const fetchAssessmentData = async (id, year) => {
        if (!id) return;
        try {
            const res = await axios.get(
                `${API_LoginURL}Sspi/SspiCalculatePoint?userId=${id}&year=${year}`,
                headers
            );
            const transformed = transformAPIData(res.data);
            setAssessmentData(transformed);
            setError("");
        } catch (err) {
            console.error("Error fetching assessment data:", err);
            setError("Failed to fetch assessment data.");
        }
    };

    useEffect(() => {
        if (student?.userName) {
            setUserId(null);
            fetchUserId();
        }
    }, [student?.userName]);

    useEffect(() => {
        if (userId && selectedYear) {
            fetchAssessmentData(userId, selectedYear);
        }
    }, [userId, selectedYear]);

    return (
        <div className="container-scroller">
            <Navbar />
            <div className="container-fluid page-body-wrapper">
                <SideBar />
                <div className="main-panel">
                    <Box
                        sx={{
                            height: "calc(100vh - 60px)",
                            padding: "20px",
                            backgroundColor: "#F8F9FD",
                            overflowY: "scroll",
                        }}
                    >
                        <Container
                            disableGutters
                            maxWidth={false}
                            sx={{
                                bgcolor: "#F5F7FF",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    height: "100%",
                                    width: "100%",
                                }}
                            >
                                <Box sx={{ flexGrow: 1 }}>
                                    {/* Student Info Cards */}
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "repeat(4, 1fr)",
                                            gap: 3,
                                        }}
                                    >
                                        {stat.map((item, idx) => (
                                            <Card key={idx}>
                                                <CardContent
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "start",
                                                        alignItems: "center",
                                                        gap: 2,
                                                    }}
                                                >
                                                    <img
                                                        alt={`${item.title} icon`}
                                                        src={item.icon}
                                                        style={{
                                                            width: "60px",
                                                            height: "60px",
                                                        }}
                                                    />
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {item.title}
                                                        </Typography>
                                                        <Typography variant="h6">
                                                            {item.val}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>

                                    {/* Error Message */}
                                    {error && (
                                        <Typography
                                            color="error"
                                            sx={{ mt: 2 }}
                                        >
                                            {error}
                                        </Typography>
                                    )}

                                    {/* Assessment Tabs */}
                                    <Tabs
                                        value={selectedTab}
                                        onChange={(e, newValue) =>
                                            setSelectedTab(newValue)
                                        }
                                        sx={{ mt: 3 }}
                                    >
                                        <Tab label="Initial Assessment" />
                                        <Tab label="Detailed Assessment" />
                                        <Tab label="Half Yearly Exam" />
                                        <Tab label="Final Exam" />
                                    </Tabs>

                                    {/* Graphs + SSPI */}
                                    <Container
                                        disableGutters
                                        maxWidth={false}
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 2,
                                            mt: 2,
                                        }}
                                    >
                                        {/* Bar Chart */}
                                        <Box
                                            sx={{ flex: 1, minWidth: "300px" }}
                                        >
                                            <Card>
                                                <CardContent>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent:
                                                                "space-between",
                                                            alignItems:
                                                                "center",
                                                            flexWrap: "wrap",
                                                            mb: 2,
                                                        }}
                                                    >
                                                        <Typography variant="h6">
                                                            {assessmentData?.[
                                                                selectedKey
                                                            ]?.title || ""}
                                                        </Typography>

                                                        <FormControl
                                                            sx={{
                                                                minWidth: 120,
                                                            }}
                                                            size="small"
                                                        >
                                                            <InputLabel id="year-label">
                                                                Year
                                                            </InputLabel>
                                                            <Select
                                                                labelId="year-label"
                                                                value={
                                                                    selectedYear
                                                                }
                                                                label="Year"
                                                                onChange={(e) =>
                                                                    setSelectedYear(
                                                                        Number(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    )
                                                                }
                                                            >
                                                                {yearOptions.map(
                                                                    (year) => (
                                                                        <MenuItem
                                                                            key={
                                                                                year
                                                                            }
                                                                            value={
                                                                                year
                                                                            }
                                                                        >
                                                                            {
                                                                                year
                                                                            }
                                                                        </MenuItem>
                                                                    )
                                                                )}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>

                                                    {assessmentData && (
                                                        <Box
                                                            sx={{
                                                                width: "100%",
                                                                height: 300,
                                                            }}
                                                        >
                                                            <Bar
                                                                data={
                                                                    assessmentData[
                                                                        selectedKey
                                                                    ].data
                                                                }
                                                                options={
                                                                    barOptions
                                                                }
                                                                height={300}
                                                            />
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Box>

                                        {/* Speedometer */}
                                        <Box
                                            sx={{ flex: 1, minWidth: "300px" }}
                                        >
                                            <Card sx={{ height: "100%" }}>
                                                <CardContent>
                                                    <Typography
                                                        variant="h6"
                                                        gutterBottom
                                                    >
                                                        STEM Skill Power Index
                                                        (SSPI)™
                                                    </Typography>
                                                    {assessmentData && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent:
                                                                    "center",
                                                                alignItems:
                                                                    "center",
                                                                mt: 2,
                                                            }}
                                                        >
                                                            <Speedometer
                                                                value={
                                                                    assessmentData[
                                                                        selectedKey
                                                                    ].average ||
                                                                    0
                                                                }
                                                                minValue={0}
                                                                maxValue={100}
                                                                needleColor="steelblue"
                                                                startColor="red"
                                                                endColor="green"
                                                                segments={10}
                                                                width={250}
                                                                height={160}
                                                                valueTextFontSize="20px"
                                                            />
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    </Container>
                                </Box>
                            </Box>
                        </Container>
                    </Box>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
